import type {FC} from 'react';
import moment from 'moment';
import type {RecoveryDate} from 'api/v2';
import {Box, Typography} from '@wellstone-solutions/web';
import {Theme} from '@wellstone-solutions/common';

interface RecoveryHistoryItemProps {
  recoveryDate: RecoveryDate;
}

// Recovery dates are stored at midnight UTC; format in UTC so viewers west
// of UTC don't see the previous day.
const formatDate = (value: string) =>
  moment.utc(value).format('ddd MMM D, YYYY');

export const RecoveryHistoryItem: FC<RecoveryHistoryItemProps> = ({
  recoveryDate,
}) => {
  const {
    recoveryDate: newDate,
    previousRecoveryDate,
    deltaDays,
    daysBeforeChange,
    note,
    created,
  } = recoveryDate;

  return (
    <Box sx={styles.container} data-testid="recovery-history-item">
      <Typography variant="caption" color={Theme.colorPalette.mediumDark}>
        {moment(created).format('MMM D, YYYY h:mm A')}
      </Typography>

      <Typography variant="body1">
        Date set to <strong>{formatDate(newDate)}</strong>
        {previousRecoveryDate && <> (was {formatDate(previousRecoveryDate)})</>}
      </Typography>

      {previousRecoveryDate && (
        <Typography variant="body2" color={Theme.colorPalette.mediumDark}>
          Counter moved {deltaDays > 0 ? `+${deltaDays}` : deltaDays} days
          &middot; streak was {daysBeforeChange}{' '}
          {daysBeforeChange === 1 ? 'day' : 'days'} at the time
        </Typography>
      )}

      {note && (
        <Typography variant="body2" sx={styles.note}>
          &ldquo;{note}&rdquo;
        </Typography>
      )}
    </Box>
  );
};

const styles = {
  container: {
    borderBottom: `1px solid ${Theme.colorPalette.light}`,
    paddingY: 1.5,
  },
  note: {
    fontStyle: 'italic',
    marginTop: 0.5,
  },
};
